import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Breadcrumb } from 'antd';
import { HomeOutlined, RightOutlined } from '@ant-design/icons';
import './ResponsiveBreadcrumb.css';

interface BreadcrumbItem {
  path: string;
  title: string;
  icon?: React.ReactNode;
}

const routeToBreadcrumb: Record<string, BreadcrumbItem> = {
  '/': { path: '/', title: 'Home', icon: <HomeOutlined /> },
  '/pricing': { path: '/pricing', title: 'Pricing' },
  '/lookup': { path: '/lookup', title: 'Case Lookup' },
  '/case': { path: '/case', title: 'Case Details' },
  '/admin': { path: '/admin', title: 'Dashboard' },
  '/admin/reports': { path: '/admin/reports', title: 'Reports' },
  '/admin/users': { path: '/admin/users', title: 'Users' },
  '/admin/settings': { path: '/admin/settings', title: 'Settings' },
  '/auth/login': { path: '/auth/login', title: 'Login' },
  '/auth/signup': { path: '/auth/signup', title: 'Sign Up' },
};

const ResponsiveBreadcrumb: React.FC = () => {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(segment => segment);

  // Build breadcrumb items
  const breadcrumbItems = [];
  let currentPath = '';

  // Always add home
  breadcrumbItems.push(routeToBreadcrumb['/']);

  // Add each segment
  pathSegments.forEach((segment) => {
    currentPath += `/${segment}`;
    const breadcrumbConfig = routeToBreadcrumb[currentPath];
    
    if (breadcrumbConfig) {
      breadcrumbItems.push(breadcrumbConfig);
    } else {
      // Handle dynamic routes
      if (currentPath.startsWith('/case/')) {
        breadcrumbItems.push({
          path: currentPath,
          title: 'Case ' + segment.substring(0, 8) + '...',
        });
      } else {
        // Fallback: capitalize segment
        breadcrumbItems.push({
          path: currentPath,
          title: segment.charAt(0).toUpperCase() + segment.slice(1),
        });
      }
    }
  });

  // Don't show breadcrumb on home page or if only one item
  if (location.pathname === '/' || breadcrumbItems.length <= 1) {
    return null;
  }

  return (
    <div className="responsive-breadcrumb-wrapper">
      {/* Desktop Breadcrumb */}
      <Breadcrumb 
        className="responsive-breadcrumb desktop"
        separator={<RightOutlined className="breadcrumb-separator" />}
      >
        {breadcrumbItems.map((item, index) => (
          <Breadcrumb.Item key={item.path}>
            {index === breadcrumbItems.length - 1 ? (
              <span className="breadcrumb-current">
                {item.icon && <span className="breadcrumb-icon">{item.icon}</span>}
                {item.title}
              </span>
            ) : (
              <Link to={item.path} className="breadcrumb-link">
                {item.icon && <span className="breadcrumb-icon">{item.icon}</span>}
                {item.title}
              </Link>
            )}
          </Breadcrumb.Item>
        ))}
      </Breadcrumb>

      {/* Mobile Breadcrumb - Show only current and parent */}
      <div className="responsive-breadcrumb mobile">
        {breadcrumbItems.length > 1 && (
          <Link 
            to={breadcrumbItems[breadcrumbItems.length - 2].path} 
            className="mobile-breadcrumb-back"
          >
            <span className="mobile-back-arrow">←</span>
            Back to {breadcrumbItems[breadcrumbItems.length - 2].title}
          </Link>
        )}
        <div className="mobile-breadcrumb-current">
          {breadcrumbItems[breadcrumbItems.length - 1].title}
        </div>
      </div>
    </div>
  );
};

export default ResponsiveBreadcrumb;